import React, { useState } from 'react';
import { usePlayer } from '../../context/PlayerContext';
import { Calendar, Trash2, AlertTriangle, CheckCircle, AlertCircle, ChevronDown, ChevronUp, X, PenTool, Eye } from 'lucide-react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import EditGameModal from './EditGameModal';
import GameSummaryModal from './GameSummaryModal';
import { formatBattingStat } from '../../utils/calculations';

const GameList = () => {
  const { 
    playerData, 
    removeGame, 
    fetchPlayerData, 
    activeSeason,
    viewingAllSeasons 
  } = usePlayer();
  
  // State for managing UI
  const [expandedGameId, setExpandedGameId] = useState(null);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [gameToEdit, setGameToEdit] = useState(null);
  const [gameToView, setGameToView] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [expandedSection, setExpandedSection] = useState('all'); // 'all', 'season', 'regular'
  
  // Helper functions for formatting
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown date';
      
      // Handle different date formats
      const date = new Date(dateString);
      return isNaN(date.getTime()) 
        ? dateString 
        : format(date, 'MMM d, yyyy');
    } catch (error) {
      return dateString || 'Unknown date';
    }
  };
  
  // Format batting line (H-AB format)
  const formatBattingLine = (stats) => {
    if (!stats) return 'No batting stats';

    const hits = Number(stats.hits) || 0;
    const atBats = Number(stats.atBats) || 0;

    return `${hits}-${atBats}${atBats > 0
      ? ` (${formatBattingStat(hits / atBats)})`
      : ''}`;
  };
  
  // Format pitching line (IP, ER, K)
  const formatPitchingLine = (stats) => {
    if (!stats) return 'No pitching stats';
    
    const ip = Number(stats.inningsPitched) || 0;
    const er = Number(stats.earnedRuns) || 0;
    const k = Number(stats.strikeouts) || 0;
    
    return `${ip.toFixed(1)} IP, ${er} ER, ${k} K`;
  };
  
  // Handle game expansion/collapse
  const toggleGameDetails = (gameId) => {
    setExpandedGameId(expandedGameId === gameId ? null : gameId);
  };
  
  // Start deletion process (show confirmation)
  const handleDeleteClick = (game, isSeasonGame) => {
    setGameToDelete({ ...game, isSeasonGame });
    setShowConfirmDialog(true);
  };
  
  // Handle view game details
  const handleViewGame = (game) => {
    setGameToView(game);
    setShowSummaryModal(true);
  };

  // Handle edit button click
  const handleEditClick = (game) => {
    setGameToEdit(game);
    setShowEditModal(true);
  };
  
  // Cancel edit
  const cancelEdit = () => {
    setGameToEdit(null);
    setShowEditModal(false);
  };

  // Close summary modal
  const closeSummaryModal = () => {
    setGameToView(null);
    setShowSummaryModal(false);
  };
  
  // Cancel deletion
  const cancelDelete = () => {
    setGameToDelete(null);
    setShowConfirmDialog(false);
  };
  
  // Confirm and execute deletion
  const confirmDelete = async () => {
    if (!gameToDelete) return;
    
    try {
      setIsDeleting(true);
      setFeedback({ message: '', type: '' });
      
      // Handle season game vs regular game deletion
      if (gameToDelete.isSeasonGame) {
        await removeGame(gameToDelete.id, activeSeason?.id);
      } else {
        await removeGame(gameToDelete.id);
      }
      
      // Update feedback and refresh data
      setFeedback({
        message: 'Game deleted successfully',
        type: 'success'
      });
      
      // Refresh player data to update stats
      await fetchPlayerData();
      
    } catch (error) {
      console.error('Error deleting game:', error);
      setFeedback({
        message: `Failed to delete game: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setIsDeleting(false);
      setShowConfirmDialog(false);
      setGameToDelete(null);
      
      // Clear feedback after 5 seconds
      setTimeout(() => {
        setFeedback({ message: '', type: '' });
      }, 5000);
    }
  };
  
  // Get games based on current section
  const getGamesToDisplay = () => {
    if (expandedSection === 'season') {
      return playerData?.seasonGames || [];
    } else if (expandedSection === 'regular') {
      return playerData?.recentGames?.filter(game => !game.seasonId) || [];
    }
    // Default: show all games
    return [
      ...(playerData?.seasonGames || []),
      ...(playerData?.recentGames?.filter(game => !game.seasonId) || [])
    ];
  };
  
  // Calculate totals for display
  const getGameCounts = () => {
    const seasonGames = playerData?.seasonGames?.length || 0;
    const regularGames = (playerData?.recentGames?.filter(g => !g.seasonId)?.length || 0);
    const totalGames = seasonGames + regularGames;
    
    return { seasonGames, regularGames, totalGames };
  };
  
  const { seasonGames, regularGames, totalGames } = getGameCounts();
  const gamesToDisplay = getGamesToDisplay();
  
  // Sort games by date (newest first)
  const sortedGames = [...gamesToDisplay].sort((a, b) => {
    const dateA = new Date(a.date || 0);
    const dateB = new Date(b.date || 0);
    return dateB - dateA;
  });
  
  return (
    <div className="bg-white dark:bg-dark-200 rounded-lg shadow-sm border border-gray-200 dark:border-dark-400 p-4">
      <h2 className="text-lg font-bold mb-4 dark:text-darkText-primary">Game Management</h2>
      
      {/* Feedback message */}
      {feedback.message && (
        <div className={`mb-4 p-3 rounded-md flex items-start ${
          feedback.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30' 
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30'
        }`}>
          {feedback.type === 'success' ? (
            <CheckCircle size={18} className="text-green-500 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertCircle size={18} className="text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" />
          )}
          <p className={`text-sm ${
            feedback.type === 'success' 
              ? 'text-green-600 dark:text-green-400' 
              : 'text-red-600 dark:text-red-400'
          }`}>
            {feedback.message}
          </p>
          <button 
            onClick={() => setFeedback({ message: '', type: '' })}
            className="ml-auto text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <X size={16} />
          </button>
        </div>
      )}
      
      {/* Section tabs */}
      <div className="flex border-b border-gray-200 dark:border-dark-400 mb-4">
        <button
          onClick={() => setExpandedSection('all')}
          className={`px-4 py-2 text-sm font-medium ${
            expandedSection === 'all'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 dark:text-darkText-tertiary'
          }`}
        >
          All Games ({totalGames})
        </button>
        {activeSeason && (
          <button
            onClick={() => setExpandedSection('season')}
            className={`px-4 py-2 text-sm font-medium ${
              expandedSection === 'season'
                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                : 'text-gray-500 dark:text-darkText-tertiary'
            }`}
          >
            Season Games ({seasonGames})
          </button>
        )}
        <button
          onClick={() => setExpandedSection('regular')}
          className={`px-4 py-2 text-sm font-medium ${
            expandedSection === 'regular'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 dark:text-darkText-tertiary'
          }`}
        >
          Regular Games ({regularGames})
        </button>
      </div>
      
      {/* Game list */}
      {sortedGames.length > 0 ? (
        <div className="space-y-3">
          {sortedGames.map(game => (
            <div 
              key={game.id} 
              className="border border-gray-200 dark:border-dark-400 rounded-md overflow-hidden"
            >
              {/* Game header */}
              <div 
                className={`p-3 flex items-center justify-between cursor-pointer ${
                  game.seasonId
                    ? 'bg-blue-50 dark:bg-blue-900/10'
                    : 'bg-gray-50 dark:bg-dark-300'
                }`}
                onClick={() => handleViewGame(game)}
              >
                <div className="flex items-center">
                  {game.seasonId && (
                    <Calendar size={16} className="text-blue-500 dark:text-blue-400 mr-2" />
                  )}
                  <div>
                    <h3 className="font-medium dark:text-darkText-primary">
                      {game.opponent || 'Unknown Opponent'}
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-darkText-tertiary">
                      {formatDate(game.date)}
                      {game.seasonId && activeSeason && (
                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                          {activeSeason.name}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center">
                  {/* Summary stats */}
                  <div className="mr-4 text-right hidden sm:block">
                    {game.battingStats && (
                      <div className="text-sm dark:text-darkText-secondary">
                        Batting: {formatBattingLine(game.battingStats)}
                      </div>
                    )}
                    {game.pitchingStats && (
                      <div className="text-sm dark:text-darkText-secondary">
                        Pitching: {formatPitchingLine(game.pitchingStats)}
                      </div>
                    )}
                  </div>
                  
                  {/* View details button */}
                  <Eye size={20} className="text-gray-400 dark:text-gray-500" />
                </div>
              </div>
              
              {/* Expanded game details */}
              {expandedGameId === game.id && (
                <div className="p-4 border-t border-gray-200 dark:border-dark-400">
                  {/* Game details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Batting stats */}
                    {game.battingStats && (
                      <div>
                        <h4 className="font-medium mb-2 dark:text-darkText-primary">Batting Stats</h4>
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">At Bats:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.atBats || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Hits:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.hits || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Doubles:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.doubles || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Triples:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.triples || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Home Runs:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.homeRuns || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">RBI:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.rbi || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Walks:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.walks || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Strikeouts:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.strikeouts || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Hit By Pitch:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.battingStats.hitByPitch || 0}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Pitching stats */}
                    {game.pitchingStats && (
                      <div>
                        <h4 className="font-medium mb-2 dark:text-darkText-primary">Pitching Stats</h4>
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Innings Pitched:</span>
                            <span className="font-medium dark:text-darkText-secondary">{Number(game.pitchingStats.inningsPitched || 0).toFixed(1)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Earned Runs:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.pitchingStats.earnedRuns || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Strikeouts:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.pitchingStats.strikeouts || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Walks:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.pitchingStats.walks || 0}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-darkText-tertiary">Hits Allowed:</span>
                            <span className="font-medium dark:text-darkText-secondary">{game.pitchingStats.hits || 0}</span>
                          </div>
                          {game.pitchingStats.inningsPitched > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-darkText-tertiary">ERA:</span>
                              <span className="font-medium dark:text-darkText-secondary">
                                {((game.pitchingStats.earnedRuns * 9) / game.pitchingStats.inningsPitched).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Game notes if available */}
                  {game.notes && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2 dark:text-darkText-primary">Notes</h4>
                      <p className="text-sm text-gray-700 dark:text-darkText-secondary bg-gray-50 dark:bg-dark-300 p-3 rounded-md">
                        {game.notes}
                      </p>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-4 flex justify-end space-x-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(game);
                      }}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400"
                    >
                      <PenTool size={16} className="mr-1" />
                      Edit Game
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(game, !!game.seasonId);
                      }}
                      className="flex items-center text-sm text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                    >
                      <Trash2 size={16} className="mr-1" />
                      Delete Game
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-darkText-tertiary">No games found.</p>
          <p className="text-sm text-gray-400 dark:text-darkText-quaternary mt-2">
            Once you add games, they will appear here.
          </p>
        </div>
      )}
      
      {/* Deletion confirmation dialog */}
      {showConfirmDialog && gameToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-dark-200 rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <AlertTriangle size={24} className="text-red-500 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg dark:text-darkText-primary">Confirm Delete</h3>
                <p className="text-gray-600 dark:text-darkText-secondary mt-1">
                  Are you sure you want to delete this game against <span className="font-medium">{gameToDelete.opponent || 'Unknown'}</span> from {formatDate(gameToDelete.date)}?
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  This action cannot be undone, and all stats for this game will be permanently removed.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={cancelDelete}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 dark:border-dark-400 rounded-md text-gray-700 dark:text-darkText-primary hover:bg-gray-50 dark:hover:bg-dark-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-opacity-50 rounded-full mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} className="mr-2" />
                    Delete Game
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Game Modal */}
      {showEditModal && gameToEdit && (
        <EditGameModal 
          game={gameToEdit}
          onClose={() => {
            setGameToEdit(null);
            setShowEditModal(false);
          }}
          onSuccess={() => {
            setGameToEdit(null);
            setShowEditModal(false);
            setFeedback({
              message: 'Game updated successfully',
              type: 'success'
            });
            
            // Clear feedback after 5 seconds
            setTimeout(() => {
              setFeedback({ message: '', type: '' });
            }, 5000);
          }}
        />
      )}

      {/* Game Summary Modal */}
      {showSummaryModal && gameToView && (
        <GameSummaryModal
          game={gameToView}
          onClose={closeSummaryModal}
          onEdit={handleEditClick}
        />
      )}
    </div>
  );
};

// PropTypes for type safety
GameList.propTypes = {
  onGameDeleted: PropTypes.func
};

export default GameList;
